/**
 * Geometría de la vista de armarios (rack frontal).
 *
 * Cada equipo se dibuja como una banda de `size_u` unidades sobre la altura
 * real del rack, y sus puertos en la cara frontal. Es una vista DISTINTA del
 * mismo grafo: los cables y las conexiones son los mismos que en el lienzo.
 */

export const U_H = 14;
export const RACK_HEADER = 30;
export const RACK_PAD = 12;
export const RACK_GAP = 120;
export const RACK_MIN_W = 460;
export const RACK_INFO_W = 104;
export const RACK_PORT_W = 10;
export const RACK_PORT_H = 10;
export const RACK_PORT_GAP = 2;
export const RACK_MAX_COLS = 24;

/**
 * Rejilla de puertos de un equipo en el frontal del rack.
 * 24 puertos van en una fila; 48, en 24 arriba + 24 abajo (como el switch real).
 */
export function rackCardGrid(portCount, sizeU, cardWidth) {
  const count = Math.max(0, portCount || 0);
  // La banda ocupa exactamente sus U (menos 2 px de separación con la de al
  // lado): si fuera más alta, dos equipos contiguos se solaparían.
  const height = Math.max(U_H * Math.max(1, sizeU || 1) - 2, 8);
  const cols = Math.min(RACK_MAX_COLS, Math.max(1, count));
  const rows = Math.max(1, Math.ceil(count / cols));
  const rowH = height / rows;
  // Un puerto nunca puede ser más alto que su fila: con 48 bocas en 1U la
  // rejilla se reduce y sigue siendo clicable al hacer zoom.
  const portH = Math.min(RACK_PORT_H, Math.max(4, rowH - 1));
  const portW = Math.min(RACK_PORT_W, portH);
  const gridW = count ? cols * portW + (cols - 1) * RACK_PORT_GAP : 0;
  const padX = Math.max(RACK_INFO_W, cardWidth - gridW - 10);
  return {
    mode: 'rack',
    width: cardWidth,
    height,
    cols,
    rows,
    rowH,
    padX,
    gridW,
    portW,
    portH,
    portGap: RACK_PORT_GAP,
    rowSizes: Array.from({ length: rows }, (_, r) => Math.min(cols, count - r * cols)),
    numbering: 'rows',
  };
}

/** Centro del puerto relativo a la esquina del equipo. */
export function rackPortOffset(geo, index) {
  const col = index % geo.cols;
  const row = Math.floor(index / geo.cols);
  return {
    x: geo.padX + col * (geo.portW + geo.portGap) + geo.portW / 2,
    y: row * geo.rowH + geo.rowH / 2,
  };
}

/** Borde de salida del latiguillo: primera fila arriba, resto abajo. */
export function rackExitSide(geo, index, selfY, otherY) {
  if (geo.rows > 1) return Math.floor(index / geo.cols) === 0 ? -1 : 1;
  return otherY < selfY ? -1 : 1;
}

/**
 * Coloca los racks lado a lado y cada equipo en su U.
 *
 * Devuelve posiciones y geometrías equivalentes a las del lienzo, para que el
 * render de cables, el resaltado y los clics funcionen sin cambios.
 */
export function buildRackLayout(nodes, rackMetas, origin = { x: 40, y: 40 }) {
  const positions = {};
  const geos = new Map();
  const boxes = [];
  const boxByNode = new Map();
  const boxByRack = new Map();

  const nodesByRack = new Map();
  (nodes || []).forEach((node) => {
    if (!node.rack_id || !node.position_u) return;
    if (!nodesByRack.has(node.rack_id)) nodesByRack.set(node.rack_id, []);
    nodesByRack.get(node.rack_id).push(node);
  });

  const metaById = new Map((rackMetas || []).map((rack) => [rack.id, rack]));
  const ordered = [...nodesByRack.keys()]
    .map((rackId) => {
      const meta = metaById.get(rackId);
      const sample = nodesByRack.get(rackId)[0];
      return {
        id: rackId,
        name: meta?.name || sample?.rack_name || `Armario ${rackId}`,
        code: meta?.code || '',
        height_u: meta?.height_u || 42,
      };
    })
    .sort((a, b) => `${a.code}${a.name}`.localeCompare(`${b.code}${b.name}`));

  let x = origin.x;
  ordered.forEach((rack) => {
    const heightU = Math.max(1, rack.height_u || 42);
    const width = RACK_MIN_W;
    const height = RACK_HEADER + heightU * U_H + RACK_PAD;
    const box = {
      key: `rack:${rack.id}`,
      rackId: rack.id,
      title: rack.code ? `${rack.code} · ${rack.name}` : rack.name,
      name: rack.name,
      code: rack.code,
      x,
      y: origin.y,
      width,
      height,
      heightU,
    };
    boxes.push(box);
    boxByRack.set(rack.id, box);

    const rackNodes = nodesByRack.get(rack.id)
      .slice()
      .sort((a, b) => (b.position_u || 0) - (a.position_u || 0));
    rackNodes.forEach((node) => {
      const sizeU = Math.max(1, node.size_u || 1);
      const startU = Math.max(1, node.position_u || 1);
      const y = box.y + RACK_HEADER + (heightU - (startU + sizeU - 1)) * U_H;
      const geo = rackCardGrid((node.ports || []).length, sizeU, width - RACK_PAD * 2);
      positions[node.key] = { x: x + RACK_PAD, y };
      geos.set(node.key, geo);
      boxByNode.set(node.key, box);
    });

    x += width + RACK_GAP;
  });

  return { positions, geos, boxes, boxByNode, boxByRack };
}

/**
 * Ruta de un cable en la vista de armarios: sale al canal lateral del rack y
 * cruza al otro por el pasillo superior (o inferior), como el cableado real.
 */
export function rackCablePoints(ax, ay, bx, by, boxA, boxB, offset = 14) {
  if (!boxA || !boxB) return null;
  if (boxA.rackId === boxB.rackId) {
    const channelX = boxA.x + boxA.width + offset;
    return [
      { x: ax, y: ay },
      { x: channelX, y: ay },
      { x: channelX, y: by },
      { x: bx, y: by },
    ];
  }

  // Entre dos armarios el cable va por el HUECO del medio (por eso se dejan
  // 120px): sale al canal del primero, baja/sube por el centro y entra en el
  // segundo. Rodear por arriba obliga a un recorrido falso de metros.
  const aLeft = boxA.x <= boxB.x;
  const left = aLeft ? boxA : boxB;
  const right = aLeft ? boxB : boxA;
  const outA = aLeft ? boxA.x + boxA.width + offset : boxA.x - offset;
  const inB = aLeft ? boxB.x - offset : boxB.x + boxB.width + offset;
  const mid = (left.x + left.width + right.x) / 2;
  return [
    { x: ax, y: ay },
    { x: outA, y: ay },
    { x: mid, y: ay },
    { x: mid, y: by },
    { x: inB, y: by },
    { x: bx, y: by },
  ];
}
